/**
 * Metadata cleanup — `spec.md` §11.9 steps 1–8, as pure functions.
 *
 * The channel's files are raw uploads: most carry no `DocumentAttributeAudio.title` at
 * all, so the "title" is a filename with a `@channel` suffix, an `(Official Audio)` tag
 * and a track number glued to the front. Everything here exists to get from that to a
 * `title` that is never empty and a `performer` that is either a real name or `null`.
 *
 * THE ONE INVIOLABLE RULE (`design-direction.md` §"Metadata honesty", §6 rule 6):
 * an unknown `performer` is `null`. Never `''`, never `'—'`, never `'Unknown Artist'`,
 * never `'Various'`. The UI omits the line; it does not fill it.
 *
 * PURE MODULE — no `node:*` imports.
 */
import { normalizeText } from './normalize.js';
import { DEFAULT_SPAM_RULES, SPAM_DEBRIS_HEAD_RE, SPAM_DEBRIS_TAIL_RE, SPAM_EMPTY_BRACKETS_RE, } from './spam.js';
/* ── Step 2 — extension ────────────────────────────────────────────────────── */
/**
 * The closed set of `spec.md` §11.9 step 2. Anything outside it is not an extension,
 * which is what keeps `Sizzla - Praise Ye Jah.Pt.2` intact.
 */
export const AUDIO_EXTENSIONS = Object.freeze([
    'mp3',
    'm4a',
    'mp4',
    'aac',
    'ogg',
    'oga',
    'opus',
    'flac',
    'wav',
    'wma',
]);
const EXTENSION_SET = new Set(AUDIO_EXTENSIONS);
const TRAILING_DOTTED_RE = /\.([A-Za-z0-9]{1,4})\s*$/;
/**
 * Strips known audio extensions from the tail, case-insensitively, repeatedly — raw
 * uploads really do arrive as `track.mp3.mp3` and `mix.flac.mp3`. A trailing dotted
 * fragment outside `AUDIO_EXTENSIONS` is never touched.
 */
export function stripExtension(name) {
    let out = name;
    for (let guard = 0; guard < 4; guard++) {
        const m = TRAILING_DOTTED_RE.exec(out);
        const ext = m?.[1];
        if (m === null || ext === undefined)
            break;
        if (!EXTENSION_SET.has(ext.toLowerCase()))
            break;
        out = out.slice(0, m.index);
    }
    return out;
}
/* ── Step 7 — whitespace ───────────────────────────────────────────────────── */
const NBSP_RE = /[\u00A0\u202F]/g;
const ZERO_WIDTH_RE = /[\u200B-\u200D\u2060\uFEFF]/g;
/** Normalises NBSP/narrow-NBSP to a plain space, collapses internal runs, trims. */
export function collapseWhitespace(text) {
    return text.replace(NBSP_RE, ' ').replace(ZERO_WIDTH_RE, '').replace(/\s+/g, ' ').trim();
}
/* ── Step 3 — spam ─────────────────────────────────────────────────────────── */
/**
 * `spec.md` §11.9 step 3 — channel-promo removal, applied repeatedly until the string
 * stops changing (a bounded loop: promo tails nest, `Title [@a] (t.me/b)` needs two
 * passes). Being a fixed point, the result is idempotent by construction.
 */
export function stripSpam(text, extraPatterns = []) {
    let cur = text;
    for (let guard = 0; guard < 8; guard++) {
        let next = cur;
        for (const rule of DEFAULT_SPAM_RULES) {
            next = next.replace(rule.pattern, rule.replacement);
        }
        for (const pattern of extraPatterns) {
            next = next.replace(pattern, ' ');
        }
        next = next.replace(SPAM_EMPTY_BRACKETS_RE, ' ');
        next = next
            .replace(NBSP_RE, ' ')
            .replace(ZERO_WIDTH_RE, '')
            .replace(/[^\S\n]{2,}/g, ' ');
        next = next.replace(SPAM_DEBRIS_TAIL_RE, '').replace(SPAM_DEBRIS_HEAD_RE, '').trim();
        if (next === cur)
            return cur;
        cur = next;
    }
    return cur;
}
/* ── Step 5 — features ─────────────────────────────────────────────────────── */
/*
 * Marker grammar. Three alternatives, all requiring a non-alphanumeric on the left so
 * `after`, `craft` and `Left` cannot produce a phantom guest:
 *   1. word marker + optional dot/colon + at least one space  (`feat X`, `feat. X`)
 *   2. word marker + dot/colon, space optional                (`feat.X`, `ft:X`)
 *   3. `w/`, space optional                                   (`w/X`)
 * `с уч.` is included; the leading lookbehind is codepoint-based (`\p{L}`), not `\b`,
 * because `\b` is ASCII-only and would never fire before a Cyrillic `с`.
 */
const FEAT_WORD = String.raw `(?:featuring|feat|ft|с\s+уч|при\s+уч)`;
const FEAT_LEAD = String.raw `(?<![\p{L}\p{N}])`;
const FEAT_MARKER = `(?:${FEAT_LEAD}${FEAT_WORD}(?:\\s*[.:]\\s*|\\s+)|${FEAT_LEAD}w\\/\\s*)`;
const FEAT_BRACKETED_RE = new RegExp(`[([{]\\s*${FEAT_MARKER}([^)\\]}]*)[)\\]}]`, 'giu');
const FEAT_INLINE_RE = new RegExp(`${FEAT_MARKER}([^([{)\\]}]*)`, 'giu');
const GUEST_SPLIT_RE = /\s*(?:,|&|\+|;|\band\b)\s*/giu;
const MAX_FEATURES = 8;
/** Renders a guest list the way the UI expects: `A`, `A & B`, `A, B & C`. */
function renderFeatures(features) {
    if (features.length === 0)
        return '';
    if (features.length === 1)
        return features[0] ?? '';
    const head = features.slice(0, -1).join(', ');
    const tail = features[features.length - 1] ?? '';
    return `${head} & ${tail}`;
}
function pushGuests(into, seen, blob) {
    for (const part of blob.split(GUEST_SPLIT_RE)) {
        const name = collapseWhitespace(part)
            .replace(SPAM_DEBRIS_TAIL_RE, '')
            .replace(SPAM_DEBRIS_HEAD_RE, '')
            .trim();
        if (name === '' || name.length < 2)
            continue;
        const key = name.toLowerCase();
        if (seen.has(key))
            continue;
        if (into.length >= MAX_FEATURES)
            return;
        seen.add(key);
        into.push(name);
    }
}
/**
 * `spec.md` §11.9 step 5. Extracts every guest-artist clause — bracketed
 * (`(feat. X)`, `[ft X]`) and inline (`… feat. X, Y & Z`) — and returns both the text
 * with the clause removed (`base`, what a performer field wants) and the text with a
 * single canonical trailing ` (feat. …)` (`text`, what a title wants).
 */
export function normalizeFeatures(input) {
    const features = [];
    const seen = new Set();
    let work = input.replace(FEAT_BRACKETED_RE, (_m, guests) => {
        pushGuests(features, seen, guests);
        return ' ';
    });
    work = work.replace(FEAT_INLINE_RE, (_m, guests) => {
        pushGuests(features, seen, guests);
        return ' ';
    });
    const base = collapseWhitespace(work.replace(SPAM_EMPTY_BRACKETS_RE, ' '))
        .replace(SPAM_DEBRIS_TAIL_RE, '')
        .trim();
    const rendered = renderFeatures(features);
    return {
        base,
        features,
        text: rendered === '' ? base : collapseWhitespace(`${base} (feat. ${rendered})`),
    };
}
/* ── Step 6 — brackets ─────────────────────────────────────────────────────── */
/** Inner texts worth keeping (`spec.md` §11.9 step 6, preserve list). */
const PRESERVE_INNER = Object.freeze([
    /remix/i,
    /version/i,
    /\bdub\b/i,
    /dubplate/i,
    /instrumental/i,
    /riddim/i,
    /\bmix(?:es|ed)?\b/i,
    /\bedit\b/i,
    /\bvip\b/i,
    /refix/i,
    /\blive\b/i,
    /acoustic/i,
    /\bfeat\b/i,
    /\bft\b/i,
    /sped\s*up/i,
    /slowed/i,
    /^(?:19|20)\d{2}$/,
]);
/** Inner texts that are unambiguously junk. The default is to drop anyway — this list
 *  exists so the intent of §11.9 step 6's drop column is legible in code. */
const DROP_INNER = Object.freeze([
    /official/i,
    /^audio$/i,
    /^hq$/i,
    /^hd$/i,
    /lyric/i,
    /free\s*download/i,
    /\d{2,4}\s*kbps/i,
    /^\d{2,4}$/,
    /www\./i,
    /^mp3$/i,
    /^@[A-Za-z0-9_]+$/,
]);
function keepBracket(inner, extraPatterns) {
    const trimmed = collapseWhitespace(inner);
    if (trimmed === '')
        return false;
    // "anything that becomes empty after the spam strip" is junk by definition.
    if (stripSpam(trimmed, extraPatterns) === '')
        return false;
    for (const re of PRESERVE_INNER)
        if (re.test(trimmed))
            return true;
    for (const re of DROP_INNER)
        if (re.test(trimmed))
            return false;
    // §11.9 step 6: "Preserve semantically meaningful parentheticals, drop the rest."
    // The preserve list is a closed set on purpose; an unrecognised parenthetical is
    // noise far more often than it is information.
    return false;
}
const BRACKET_GROUP_RE = /\(([^()]*)\)/g;
/**
 * `spec.md` §11.9 step 6 — normalise every bracket flavour to `()`, collapse doubles,
 * keep the semantically meaningful parentheticals and drop the rest.
 */
export function cleanBrackets(text, extraPatterns = []) {
    let work = text.replace(/[[{]/g, '(').replace(/[\]}]/g, ')');
    for (let guard = 0; guard < 4; guard++) {
        const next = work.replace(/\(\(([^()]*)\)\)/g, '($1)');
        if (next === work)
            break;
        work = next;
    }
    work = work.replace(BRACKET_GROUP_RE, (_m, inner) => keepBracket(inner, extraPatterns) ? `(${collapseWhitespace(inner)})` : ' ');
    work = work.replace(SPAM_EMPTY_BRACKETS_RE, ' ');
    return collapseWhitespace(work)
        .replace(SPAM_DEBRIS_TAIL_RE, '')
        .replace(SPAM_DEBRIS_HEAD_RE, '')
        .trim();
}
/* ── Step 4 — Artist - Title ───────────────────────────────────────────────── */
/**
 * Separators, in the priority order of `spec.md` §11.9 step 4. A bare unspaced `-` is
 * deliberately absent: it would destroy `Anti-Social`, `Hip-Hop` and `Buju-Banton`.
 */
const SEPARATORS = Object.freeze([' — ', ' – ', ' - ', '–', '—', ' _ ', ' | ']);
const TRACK_NUMBER_RE = /^\s*\d{1,3}(?:\s*[.)]\s*|\s+[-–—]\s+|_+\s*)/;
/** `03. `, `03 - `, `03_`, `03) ` — stripped before the split (§11.9 step 4).
 *  A four-digit leader is never a track number, so `1990 - X` keeps its year. */
function stripTrackNumber(text) {
    const out = text.replace(TRACK_NUMBER_RE, '');
    return out.trim() === '' ? text : out;
}
/** Underscore-as-space tidy-up, applied to a side AFTER the split so the ` _ `
 *  separator stays reachable. */
function tidyPart(text) {
    return collapseWhitespace(text.replace(/_+/g, ' '))
        .replace(SPAM_DEBRIS_TAIL_RE, '')
        .replace(SPAM_DEBRIS_HEAD_RE, '')
        .trim();
}
const HAS_LETTER_RE = /\p{L}/u;
/** The first separator, in `SEPARATORS` priority order, that yields two sides of at
 *  least two characters. Splits at the FIRST occurrence of that separator. */
function firstSplit(source) {
    for (const sep of SEPARATORS) {
        const at = source.indexOf(sep);
        if (at <= 0)
            continue;
        const left = tidyPart(source.slice(0, at));
        const right = tidyPart(source.slice(at + sep.length));
        if (left.length < 2 || right.length < 2)
            continue;
        return { left, right };
    }
    return null;
}
/**
 * `spec.md` §11.9 step 4. Returns `null` when nothing splits — `splitArtistTitle`
 * returning `null` is the signal that this string is a title, not a pair.
 *
 * `knownArtistNorms` (already `normalizeText`-ed) drives the reverse-order heuristic:
 * `Praise Ye Jah - Sizzla` becomes `Sizzla / Praise Ye Jah` with `swapped: true`.
 */
export function splitArtistTitle(text, knownArtistNorms) {
    let source = stripTrackNumber(collapseWhitespace(text));
    for (let guard = 0; guard < 3; guard++) {
        if (source === '')
            return null;
        const found = firstSplit(source);
        if (found === null)
            return null;
        /*
         * A side with no letters in it is not an artist — it is a leader the track-number
         * rule did not recognise, typically a four-digit year (`1990 - Shabba Ranks -
         * Wicked Inna Bed`) or a disc number. Drop it and split what is left, rather than
         * writing `1990` into `tracks.performer`.
         */
        if (!HAS_LETTER_RE.test(found.left)) {
            source = found.right;
            continue;
        }
        if (knownArtistNorms !== undefined && knownArtistNorms.size > 0) {
            const leftKnown = knownArtistNorms.has(normalizeText(found.left));
            const rightKnown = knownArtistNorms.has(normalizeText(found.right));
            if (rightKnown && !leftKnown) {
                return { performer: found.right, title: found.left, swapped: true };
            }
        }
        return { performer: found.left, title: found.right, swapped: false };
    }
    return null;
}
/* ── Step 7 — the shouting heuristic ──────────────────────────────────────── */
const SMALL_WORDS = new Set([
    'a',
    'an',
    'and',
    'the',
    'of',
    'in',
    'on',
    'at',
    'to',
    'for',
    'from',
    'by',
    'with',
    'vs',
    'feat',
    'ft',
    'de',
    'la',
    'le',
    'el',
    'da',
    'di',
    'du',
    'y',
    'и',
    'в',
    'на',
    'с',
    'по',
    'до',
    'из',
    'за',
]);
function titleCaseToken(token, isFirst) {
    const lower = token.toLowerCase();
    const letters = lower.replace(/[^\p{L}\p{N}]/gu, '');
    if (letters === '')
        return lower;
    if (!isFirst && SMALL_WORDS.has(letters))
        return lower;
    const at = lower.search(/[\p{L}\p{N}]/u);
    if (at === -1)
        return lower;
    return lower.slice(0, at) + lower.charAt(at).toUpperCase() + lower.slice(at + 1);
}
/**
 * `spec.md` §11.9 step 7. Only touches a string that is **entirely uppercase**,
 * **longer than 20 characters** and **contains no digits** — the caps-lock rip
 * signature. `SIZZLA` stays `SIZZLA`; a title with a `320` in it is left alone because
 * that is a tag, not shouting. Behind `INDEX_FIX_SHOUTING` (default off) by design: it
 * is a judgement call and the operator gets to overrule it.
 */
export function maybeFixShouting(text) {
    if (text.length <= 20)
        return text;
    if (/\d/u.test(text))
        return text;
    if (!/\p{Lu}/u.test(text))
        return text;
    if (text !== text.toUpperCase())
        return text;
    let isFirst = true;
    return text
        .split(' ')
        .map((token) => {
        if (token === '')
            return token;
        const out = titleCaseToken(token, isFirst);
        isFirst = false;
        return out;
    })
        .join(' ');
}
/* ── Steps 1 + 8 — the pipeline ───────────────────────────────────────────── */
const CONFIDENCE = Object.freeze({
    tags: 90,
    mixed: 70,
    filename: 60,
    titleOnly: 50,
    caption: 40,
    fallback: 20,
});
/** Cleans a tag-supplied field: extension, spam, whitespace. */
function cleanTagField(value, extra) {
    if (value === undefined)
        return '';
    return collapseWhitespace(stripSpam(stripExtension(value), extra));
}
/** Cleans a filename down to the string the split and the fallback title both use. */
function cleanFilenameBase(fileName, extra) {
    if (fileName === undefined)
        return '';
    let work = stripExtension(fileName);
    work = stripSpam(work, extra);
    // `Artist_-_Title` — underscores flanking a dash are whitespace, not content.
    work = work.replace(/_+\s*([-–—])\s*_+/g, ' $1 ');
    return collapseWhitespace(work);
}
/**
 * `spec.md` §11.9 steps 1–8. Deterministic and side-effect free: the same `RawMeta`
 * always yields the same `CleanMeta`, which is what lets the indexer re-run over the
 * whole channel without churning rows.
 */
export function cleanMetadata(input, opts = {}) {
    const extra = opts.extraSpamPatterns ?? [];
    const tagTitle = cleanTagField(input.rawTitle, extra);
    const tagPerformer = cleanTagField(input.rawPerformer, extra);
    const fileBase = cleanFilenameBase(input.fileName, extra);
    const fileSplit = splitArtistTitle(fileBase, input.knownArtistNorms);
    const captionFirstLine = collapseWhitespace(stripSpam(stripExtension((input.caption ?? '').split(/\r?\n/)[0] ?? ''), extra));
    const captionSplit = splitArtistTitle(captionFirstLine, input.knownArtistNorms);
    let picked;
    let swapped = false;
    if (tagTitle !== '' && tagPerformer !== '') {
        // 1. Both tags present — the best case, and the only 90.
        picked = {
            title: tagTitle,
            performer: tagPerformer,
            metaSource: 'tags',
            metaConfidence: CONFIDENCE.tags,
        };
    }
    else if (tagTitle !== '' && fileSplit !== null) {
        // Mixed: tag title + filename's performer.
        picked = {
            title: tagTitle,
            performer: fileSplit.performer,
            metaSource: 'mixed',
            metaConfidence: CONFIDENCE.mixed,
        };
        swapped = fileSplit.swapped;
    }
    else if (tagPerformer !== '' && fileSplit !== null) {
        // Mixed: tag performer + filename's title.
        picked = {
            title: fileSplit.title,
            performer: tagPerformer,
            metaSource: 'mixed',
            metaConfidence: CONFIDENCE.mixed,
        };
        swapped = fileSplit.swapped;
    }
    else if (tagPerformer !== '' && fileBase !== '') {
        /*
         * A performer tag with a filename that does not split. §11.9's ladder has no rung
         * for this — it would drop a perfectly good performer on the floor — so the pair is
         * taken as mixed, at the title-only confidence rather than the mixed one because
         * only half of it came from a structured source.
         */
        picked = {
            title: fileBase,
            performer: tagPerformer,
            metaSource: 'mixed',
            metaConfidence: CONFIDENCE.titleOnly,
        };
    }
    else if (fileSplit !== null) {
        // 2. Filename parses as `Artist - Title`.
        picked = {
            title: fileSplit.title,
            performer: fileSplit.performer,
            metaSource: 'filename',
            metaConfidence: CONFIDENCE.filename,
        };
        swapped = fileSplit.swapped;
    }
    else if (tagTitle !== '') {
        // 3. Title tag alone. performer stays null — never invented.
        picked = {
            title: tagTitle,
            performer: null,
            metaSource: 'tags',
            metaConfidence: CONFIDENCE.titleOnly,
        };
    }
    else if (captionSplit !== null) {
        // 4. The caption's first line parses as `Artist - Title`.
        picked = {
            title: captionSplit.title,
            performer: captionSplit.performer,
            metaSource: 'caption',
            metaConfidence: CONFIDENCE.caption,
        };
        swapped = captionSplit.swapped;
    }
    else if (fileBase === '' && captionFirstLine !== '') {
        /*
         * 5. No tags, no filename, and a caption that does not split into `Artist - Title`.
         * The caption's first line is then the only thing anyone wrote about this post, so it
         * is the title. Taken at the fallback confidence, not the caption one: rung 4 earned
         * its 40 by parsing a structure, and this parsed nothing.
         */
        picked = {
            title: captionFirstLine,
            performer: null,
            metaSource: 'caption',
            metaConfidence: CONFIDENCE.fallback,
        };
    }
    else {
        // 6. Last resort: the cleaned filename, else the literal `Untitled`.
        picked = {
            title: fileBase,
            performer: null,
            metaSource: 'filename',
            metaConfidence: CONFIDENCE.fallback,
        };
    }
    // Step 5 — features, on the title side and lifted out of the performer side.
    const titleFeat = normalizeFeatures(picked.title);
    const performerFeat = picked.performer === null ? null : normalizeFeatures(picked.performer);
    const features = [];
    const seen = new Set();
    for (const name of [...(performerFeat?.features ?? []), ...titleFeat.features]) {
        const key = name.toLowerCase();
        if (seen.has(key))
            continue;
        seen.add(key);
        features.push(name);
    }
    // Step 6 — brackets, on the title only. A performer's parenthetical is far more
    // likely to be part of the name (`Anthony B (The Fireman)`) than title noise, and
    // the drop-by-default rule would eat it.
    let title = cleanBrackets(tidyPart(titleFeat.base), extra);
    if (features.length > 0) {
        title = collapseWhitespace(`${title} (feat. ${renderFeatures(features)})`);
    }
    let performer = performerFeat === null ? null : tidyPart(stripSpam(performerFeat.base, extra));
    // Step 7 — whitespace, then the opt-in shouting fix.
    title = collapseWhitespace(title);
    if (opts.fixShouting === true) {
        title = maybeFixShouting(title);
        if (performer !== null)
            performer = maybeFixShouting(performer);
    }
    // Step 8 — the missing-field policy.
    if (title === '')
        title = 'Untitled';
    if (performer !== null && performer === '')
        performer = null;
    const metaConfidence = Math.max(0, Math.min(100, picked.metaConfidence - (swapped ? 10 : 0)));
    return { title, performer, metaSource: picked.metaSource, metaConfidence, features };
}
/* ── Step 10 support — the primary artist of a collaboration ───────────────── */
/*
 * `spec.md` §11.9 step 5 lifts `feat.`/`ft.`/`w/` guests out of the performer and into
 * the title, so by the time a performer string reaches artist linkage the only guests
 * left are the ones the uploader wrote as a *list*: `Vybz Kartel x Skeng`,
 * `Silk Boss, CJTheChemist, Abroad Boss`. §5.2 gives `tracks` exactly one `artist_id`,
 * so the list has to collapse to one name, and the first name is the one every
 * platform treats as the primary credit.
 *
 * The separator set is deliberately NARROW. `&`, `+` and `and` are excluded because
 * registered duos are spelled with them — `Chaka Demus & Pliers`, `Sly & Robbie`,
 * `Toots and the Maytals` — and splitting those invents an artist who never existed.
 * `x` and `×` require spaces on both sides so `Malcolm X` and `DJ X` survive; `vs`
 * carries the same guard.
 */
const ARTIST_LIST_SEPARATOR_RE = /\s*(?:,|;|\/|\s(?:x|×|vs\.?|versus)\s)\s*/iu;
/** Shorter than this and the "name" is initials debris, not a credit. */
const MIN_ARTIST_NAME_LENGTH = 2;
/** `112` and `3 The Hard Way` are real acts, so a digit counts as content too. */
const HAS_DIGIT_RE = /\p{N}/u;
/**
 * The primary credit inside a performer string (`spec.md` §11.9 step 10).
 *
 * `null` when the input is absent or collapses to nothing worth linking — §11.9 step 8
 * forbids inventing an `Unknown Artist` bucket, so an unresolvable performer must leave
 * `tracks.artist_id` NULL rather than point at a placeholder row.
 */
export function primaryArtist(performer) {
    if (performer === null || performer === undefined)
        return null;
    const whole = collapseWhitespace(performer);
    if (whole === '')
        return null;
    const first = whole.split(ARTIST_LIST_SEPARATOR_RE)[0] ?? '';
    const name = first.length >= MIN_ARTIST_NAME_LENGTH ? first : whole;
    if (name.length < MIN_ARTIST_NAME_LENGTH)
        return null;
    // The same guard `splitArtistTitle` applies to a candidate artist side: a name made of
    // nothing but punctuation is debris. `', , ,'` reaches here through the whole-string
    // fallback, and without this it would come back as an artist name — contained today
    // only because `resolveArtistId` re-normalises and bails, which is one caller's luck
    // rather than this function's contract.
    return HAS_LETTER_RE.test(name) || HAS_DIGIT_RE.test(name) ? name : null;
}
//# sourceMappingURL=metadata.js.map